import { flow } from "fp-ts/lib/function";
import { negate } from "lodash";
import * as Monoid from "fp-ts/Monoid";
import * as Array from "fp-ts/Array";
import { BorrowState, PositionAsset, Positions } from "./types";
import {
  BigIntWithDecimals,
  CollateralType,
  NATIVE_CURRENCY_DECIMALS,
} from "@silo/types";
import {
  BigIntWithDecimalsSum,
  convertCurrency,
  disjoin,
  isUndefined,
} from "@silo/utils";
import { match } from "ts-pattern";
import produce from "immer";
import { pipe } from "effect";

interface PositionsTotals {
  totalDepositedUSD: BigIntWithDecimals;
  totalBorrowedUSD: BigIntWithDecimals;
}

const totalsMonoid = Monoid.struct<PositionsTotals>({
  totalDepositedUSD: BigIntWithDecimalsSum,
  totalBorrowedUSD: BigIntWithDecimalsSum,
});

// Positions utilities
type PositionsUtility<T> = (positions: Positions) => T;

export const getCollateralAssets: PositionsUtility<PositionAsset[]> = ({
  assets,
}) => assets.filter(getHasEffectiveCollateral);

export const getDepositAssets: PositionsUtility<PositionAsset[]> = ({
  assets,
}) => {
  return assets.flatMap((asset) => {
    const collateralType = getCollateralType(asset);

    /**
     * In dual collateral cases, need to effectively split the asset into two separate assets that each have borrowable and protected balances
     */
    return match(collateralType)
      .with("dual", () => [
        produce(asset, (draft) => {
          draft.collateralOnlyBalance = 0n;
        }),
        produce(asset, (draft) => {
          draft.collateralBalance = 0n;
        }),
      ])
      .with("borrowable", () => [asset])
      .with("protected", () => [asset])
      .with("none", () => [])
      .exhaustive();
  });
};

export const getNonCollateralAssets: PositionsUtility<PositionAsset[]> = ({
  assets,
}) => {
  return assets.filter((asset) => {
    const hasEffectiveCollateral = getHasEffectiveCollateral(asset);

    return !hasEffectiveCollateral && !asset.isProtectedOnly;
  });
};

export const getDebtAssets: PositionsUtility<PositionAsset[]> = ({ assets }) =>
  assets.filter(getHasDebt);

export const getHasNoPositions = flow(
  getCollateralAssets,
  (assets) => !assets.length
);

export const getIsLoan = flow(getDebtAssets, (assets) => !!assets.length);

export const getIsBorrowingDisabled: PositionsUtility<boolean> = ({ assets }) =>
  assets.every(getHasEffectiveCollateral);

// PositionAsset utilities
type PositionAssetUtility<T> = (asset: PositionAsset) => T;

export const getHasEffectiveCollateral: PositionAssetUtility<boolean> = ({
  collateralBalance,
  collateralOnlyBalance,
}) => !!collateralBalance || !!collateralOnlyBalance;

export const getHasDebt: PositionAssetUtility<boolean> = ({ debtBalance }) =>
  !!debtBalance;

export const getCollateralType: PositionAssetUtility<CollateralType> = ({
  collateralBalance,
  collateralOnlyBalance,
}) => {
  if (!!collateralBalance && !!collateralOnlyBalance) {
    return "dual";
  }

  if (!!collateralBalance) return "borrowable";

  if (!!collateralOnlyBalance) return "protected";

  return "none";
};

export const getSymbol = ({ symbol }: PositionAsset) => symbol;

export const disjoinSymbols = flow(Array.map(getSymbol), disjoin);

export type LiquidationPriceState =
  | { _tag: "none" }
  | { _tag: "notApplicable" }
  | { _tag: "loading" }
  | { _tag: "loaded"; value: BigIntWithDecimals };

type GetLiquidationPriceState = (params: {
  positions: Positions;
  asset: PositionAsset;
  quotePrice: BigIntWithDecimals | undefined;
}) => LiquidationPriceState;

export const getLiquidationPriceState: GetLiquidationPriceState = ({
  positions,
  asset: { liquidationPrice },
  quotePrice,
}) => {
  if (!getIsLoan(positions)) {
    return { _tag: "none" };
  }

  if (liquidationPrice._tag === "notApplicable") {
    return { _tag: "notApplicable" };
  }

  if (isUndefined(quotePrice)) {
    return { _tag: "loading" };
  }

  const { value } = liquidationPrice;

  return {
    _tag: "loaded",
    value: convertCurrency(
      { value, decimals: NATIVE_CURRENCY_DECIMALS },
      quotePrice
    ),
  };
};

export const getBorrowState: PositionsUtility<BorrowState> = (positions) => {
  const isBorrowingDisabled = getIsBorrowingDisabled(positions);

  if (isBorrowingDisabled) {
    return { _tag: "disabled" };
  }

  const debtAssets = getDebtAssets(positions);

  if (!debtAssets.length) {
    return { _tag: "noBorrows" };
  }

  return { _tag: "openBorrows", assets: debtAssets };
};
